import { InsuranceCoverage, InsuranceType } from "../interfaces/DTO/mislaka_xml_insurence_models";
import {
  AdjustedProductType,
  PolicyOrAccount,
  Product,
} from "../interfaces/DTO/mislaka_xml_models"; // עדכן path בהתאם

export class MimshakInsurenceUtil {
  /**
   * מחזיר את שמות הכיסויים מתוך הפוליסה
   */
  static getCoverageNames(policy: PolicyOrAccount): string[] {
    const coverages = policy.insurence_coverages ?? [];
    return coverages
      .map((c) => c.coverage_name ?? c.coverage_name)
      .filter((name): name is string => !!name);
  }
  
  /**
   * מחשב סכום ביטוח בסיסי לפוליסה (מסכם את כל insurance_amount מכל basic_insurance)
   */
static getBasicInsuranceAmount(policy: PolicyOrAccount): number | null {
  const coverages = policy.insurence_coverages ?? [];
  const allBasics = coverages.flatMap((coverage) => coverage.basic_insurance ?? []);

  if (allBasics.length === 0) return null;

  return allBasics
    .map((basic) =>
      typeof basic.insurance_amount === "number" ? basic.insurance_amount : 0
    )
    .reduce((sum, amount) => sum + amount, 0);
}

  /**
   * האם יש לפחות כיסוי בסיסי עם סכום > 0
   */
  static hasBasicInsurance(policy: PolicyOrAccount): boolean {
    return MimshakInsurenceUtil.getBasicInsuranceAmount(policy) > 0;
  }

  /**
   * סופר את כל הכיסויים הרלוונטיים לפי קריטריון משופר
   */
  static countValidCoverages(policy: PolicyOrAccount): number {
    const coverages = policy.insurence_coverages ?? [];
    return coverages.filter((coverage) =>
      coverage.coverage_details != null ||
      (coverage.insurance_coverage && coverage.insurance_coverage.length > 0) ||
      (coverage.coverages && coverage.coverages.length > 0) ||
      (coverage.basic_insurance && coverage.basic_insurance.length > 0)
    ).length;
  }

  /**
   * אוסף את כל הכיסויים מכל הפוליסות במוצר
   */
  static getAllCoveragesFromProduct(product: Product): InsuranceCoverage[] {
    return (product.policies_or_accounts || [])
      .flatMap((policy) => policy.insurence_coverages ?? []);
  }

  /**
   * מחזיר את סכום הכיסוי המתוקן עבור כיסוי ביטוחי
   * מטפל במקרים מיוחדים של ביטוח מנהלים ופוליסות סיכון טהור
   * שבהם סכום הכיסוי הוא 0 אך יש כיסוי ביטוחי בסיסי
   *
   * @param coverage - הכיסוי הביטוחי
   * @param product - המוצר שמכיל את הכיסוי
   * @param policy - הפוליסה שמכילה את הכיסוי
   * @returns סכום הכיסוי המתוקן
   */
  static getAdjustedCoverageAmount(
    coverage: InsuranceCoverage,
    product: Product,
    policy: PolicyOrAccount
  ): number {
    const insuranceSum = coverage.coverage_details?.insurance_sum || 0;

    // אם יש סכום ביטוח, מחזירים אותו כמו שהוא
    if (insuranceSum !== 0) {
      return insuranceSum;
    }

    // בודקים אם יש כיסוי ביטוחי בסיסי
    const basicInsuranceAmount = MimshakInsurenceUtil.getBasicInsuranceAmount(policy);
    if (!basicInsuranceAmount || !product.details?.adjusted_product_type) {
      return insuranceSum;
    }

    // בודקים אם זה אחד מהמקרים המיוחדים
    const isManagersInsurance =
      coverage.coverage_details?.insurance_kind === InsuranceType.COMBINED_SAVINGS_PLAN &&
      (product.details.adjusted_product_type === AdjustedProductType.ACTIVE_MANAGERS_INSURANCE ||
       product.details.adjusted_product_type === AdjustedProductType.INACTIVE_MANAGERS_INSURANCE);

    const isPureRiskDeath =
      coverage.coverage_details?.insurance_kind === InsuranceType.DEATH &&
      product.details.adjusted_product_type === AdjustedProductType.PURE_RISK_POLICY;

    // אם זה אחד מהמקרים המיוחדים, מחזירים את הסכום הבסיסי
    if (/*isManagersInsurance ||*/ isPureRiskDeath) {
      return basicInsuranceAmount;
    }

    return insuranceSum;
  }

  /**
   * מנרמל את נתוני הכיסויים הביטוחיים במוצר
   * מתקן את insurance_sum לפי הלוגיקה העסקית של ביטוח מנהלים וסיכון טהור
   *
   * @param product - המוצר לנרמול
   * @returns המוצר המנורמל (אותו אובייקט, מוחזר לנוחות)
   */
  static normalizeProductCoverages(product: Product): Product {
    if (!product.policies_or_accounts || product.policies_or_accounts.length === 0) {
      return product;
    }

    product.policies_or_accounts.forEach(policy => {
      if (!policy.insurence_coverages || policy.insurence_coverages.length === 0) {
        return;
      }

      // חישוב סכום הכיסוי הבסיסי פעם אחת לכל פוליסה
      const basicInsuranceAmount = MimshakInsurenceUtil.getBasicInsuranceAmount(policy);

      policy.insurence_coverages.forEach(coverage => {
        if (!coverage.coverage_details) {
          return;
        }

        // מתקן את insurance_sum אם צריך
        const adjustedAmount = MimshakInsurenceUtil.getAdjustedCoverageAmount(
          coverage,
          product,
          policy
        );

        // עדכון בפועל של האובייקט
        if (coverage.coverage_details.insurance_sum !== adjustedAmount) {
          coverage.coverage_details.insurance_sum = adjustedAmount;
        }
      });
    });

    return product;
  }

   static getPensionCoverageSummary(policy: PolicyOrAccount): PensionCoverageSummary {
    const coverages = (policy.insurence_coverages ?? []).flatMap(cov => cov.coverages ?? []);

    return coverages.reduce<PensionCoverageSummary>(
      (acc, cov) => ({
        totalDisabilityPension: acc.totalDisabilityPension + (cov.total_disability_pension || 0),
        totalWidowPension: acc.totalWidowPension + (cov.widow_pension_amount || 0),
        totalOrphanPension: acc.totalOrphanPension + (cov.orphan_pension_amount || 0),
        totalParentSupportPension: acc.totalParentSupportPension + (cov.parent_support_pension_amount || 0),
      }),
      {
        totalDisabilityPension: null,
        totalWidowPension: null,
        totalOrphanPension: null,
        totalParentSupportPension: null,
      }
    );
  }
}
export interface PensionCoverageSummary {
  totalDisabilityPension: number | null;
  totalWidowPension: number | null;
  totalOrphanPension: number | null;
  totalParentSupportPension: number | null;
}
